import { useDecision, useExperiment } from '@optimizely/react-sdk';
import { useMemo } from 'react';
import { isOptimizelyReady } from './optimizelyClient';
import optimizelyClient from './optimizelyClient';

/**
 * Type for feature flag hook return value
 */
export interface FeatureFlagResult {
  /** Whether the feature is enabled for the current user */
  isEnabled: boolean;
  /** All variables defined for the feature */
  variables: Record<string, any>;
  /** The variation key (useful for tracking) */
  variationKey: string | null | undefined;
  /** The rule key that was matched */
  ruleKey: string | null | undefined;
  /** Whether the Optimizely client is ready */
  flagReady: boolean;
  /** Reasons for the decision (useful for debugging) */
  reasons: string[];
  /** Get a specific variable value with type safety */
  getVariable: <T>(key: string, defaultValue?: T) => T;
}

/**
 * Type for A/B test hook return value
 */
export interface ABTestResult {
  /** The variation assigned to the user */
  variation: string | null;
  /** Whether the experiment is ready */
  experimentReady: boolean;
  /** The variation index (useful for analytics) */
  variationIndex?: number;
}

/**
 * Custom hook to use Optimizely feature flags
 * 
 * @param featureKey - The key of the feature to check
 * @returns An object containing the decision, variables, and helper methods
 * 
 * @example
 * const { isEnabled, getVariable } = useFeatureFlag('new_checkout_flow');
 * if (isEnabled) {
 *   const checkoutType = getVariable('checkout_type', 'standard');
 *   // Use the feature with the specific variable
 * }
 */
export const useFeatureFlag = (featureKey: string): FeatureFlagResult => {
  // The useDecision hook takes just the feature key
  const [decision, clientReady] = useDecision(featureKey);
  
  const getVariable = useMemo(() => {
    /**
     * Get a specific variable with type safety
     * @param key The variable key
     * @param defaultValue Optional default value if variable is not found
     * @returns The variable value or default value
     */
    return <T>(key: string, defaultValue?: T): T => {
      if (!decision?.variables || !(key in decision.variables)) {
        return defaultValue as T;
      }
      return decision.variables[key] as T;
    };
  }, [decision?.variables]);
  
  return {
    isEnabled: decision?.enabled || false,
    variables: decision?.variables || {},
    variationKey: decision?.variationKey,
    ruleKey: decision?.ruleKey,
    flagReady: clientReady,
    reasons: decision?.reasons || [],
    getVariable,
  };
};

/**
 * Custom hook to use Optimizely A/B experiments
 * 
 * @param experimentKey - The key of the experiment to check
 * @returns An object containing the variation and experiment status
 * 
 * @example
 * const { variation, experimentReady } = useABTest('pricing_experiment');
 * if (experimentReady && variation === 'treatment') {
 *   // Show treatment experience
 * } else {
 *   // Show control experience
 * }
 */
export const useABTest = (experimentKey: string): ABTestResult => {
  // The useExperiment hook takes just the experiment key
  const [variation, clientReady] = useExperiment(experimentKey);
  
  // Calculate the variation index for analytics purposes
  const variationIndex = useMemo(() => {
    if (!variation) return undefined;
    
    // Common variation names and their indices
    const variationMap: Record<string, number> = {
      'control': 0,
      'treatment': 1,
      'variant_a': 0,
      'variant_b': 1,
      'variant_c': 2,
    };
    
    return variationMap[variation.toLowerCase()] ?? undefined;
  }, [variation]);
  
  return {
    variation,
    experimentReady: clientReady,
    variationIndex,
  };
};

/**
 * Helper function to check if a feature flag is enabled
 * This is useful for conditional rendering in JSX
 * 
 * @param featureKey - The key of the feature to check
 * @returns True if the feature is enabled, false otherwise
 * 
 * @example
 * {isFeatureEnabled('new_header') && <NewHeader />}
 */
export const isFeatureEnabled = (featureKey: string): boolean => {
  // This is a simple helper that doesn't use hooks
  // It can be used outside of React components
  // It will return false if Optimizely is not ready
  if (!isOptimizelyReady()) return false;
  
  try {
    const decision = optimizelyClient.decide(featureKey);
    return decision?.enabled || false;
  } catch (error) {
    console.error(`Error checking feature flag ${featureKey}:`, error);
    return false;
  }
};
